/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { call, delay, put, race } from "redux-saga/effects";

import * as ApplicationConstants from "Constants/ApplicationConstants";
import * as mvpApi from "Services/MVP/MVPApi";

import { FilterValueFieldNames } from "Constants/FilterFields";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { ICapDataResult } from "Interfaces/Demographics/ICapDataResult";
import { IDemographicsRequest } from "Interfaces/Demographics/IDemographicsRequest";
import { IDemographicsResult } from "Interfaces/Demographics/IDemographicsResult";
import { IApiErrorResult, IApiResult2 } from "Interfaces/IApiResult";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import {
  capDataLoadFail,
  capDataLoadSuccess,
  filterDemographicsFail,
  filterDemographicsSuccess,
  IFilterDemographics,
} from "./DemographicsAction";

/**
 * Fetch filtered demographics data.
 * @param {IFilterDemographics} action for fetching filtered demographics data.
 * @returns {void}
 */
function* handleFilterDemographics(action: IFilterDemographics) {
  const failedMessage = "Failed to get filtered demographics.";
  const requestBody = {
    countryRegionList: [],
    clRegionList: [],
    applicationTypes: [],
    technologyAreaList: [],
    awardCategoryList: [],
    genderList: [],
  } as IDemographicsRequest;

  action.selectedDemographicsFilterValues.forEach((item) => {
    switch (item.fieldName) {
      case ApplicationConstants.APPLICATION_TYPE:
        requestBody.applicationTypes.push(item.value);
        break;
      case ApplicationConstants.APPLICATION_COUNTRY_REGION:
        requestBody.countryRegionList.push(item.value);
        break;
      case FilterValueFieldNames.REGION:
        requestBody.clRegionList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_GROUP:
        requestBody.awardCategoryList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA:
        requestBody.technologyAreaList.push(item.value);
        break;
      case ApplicationConstants.GENDER:
        requestBody.genderList.push(item.value);
        break;
      default:
        return;
    }
  });

  try {
    const { result, timeout }: IApiResult2<IDemographicsResult> | any = yield race({
      result: call(mvpApi.getFilteredDemographicsData, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(filterDemographicsFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(filterDemographicsSuccess(result.parsedBody));
        break;
      case HttpStatusCode.BadRequest:
        yield put(filterDemographicsFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(filterDemographicsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(filterDemographicsFail(error));
  }
}

/**
 * Fetch filtered capacity data.
 * @returns {void}
 */
function* handleCapData() {
  const failedMessage = "Failed to get filtered demographics.";

  try {
    const { result, timeout }: IApiResult2<ICapDataResult> | any = yield race({
      result: call(mvpApi.getCapacityData),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(capDataLoadFail(ApplicationConstants.GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(capDataLoadSuccess(result.parsedBody));
        break;
      case HttpStatusCode.BadRequest:
        yield put(capDataLoadFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(capDataLoadFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(capDataLoadFail(error));
  }
}

export { handleFilterDemographics, handleCapData };
